import { toTask, when } from '@execonline-inc/maybe-adapter';
import { noop } from '@kofno/piper';
import { Maybe, just } from 'maybeasy';
import * as React from 'react';
import { Task } from 'taskarian';
import { emptyFragment } from '../../../../../../EmptyFragment';
import { observer } from 'mobx-react';

interface Props {
  src: string;
}

export interface ImageSize {
  width: number;
  height: number;
}

type ImageState = { kind: 'waiting' } | { kind: 'ready'; size: ImageSize } | { kind: 'error' };

const getDesiredDimension = (screenWidth: number): Maybe<number> =>
  when(screenWidth >= 1300, screenWidth / 4)
    .orElse(() => when(isMediumScreen(screenWidth), screenWidth / 3))
    .orElse(() => just(screenWidth));

const isMediumScreen = (innerWidth: number): boolean => {
  const mediumScreenWidthLowerBound = 1140;
  const mediumScreenWidthUpperBound = 1300;
  return mediumScreenWidthLowerBound < innerWidth && innerWidth < mediumScreenWidthUpperBound;
};

const whenTallImageT = (image: HTMLImageElement): Task<string, ImageSize> =>
  toTask(
    'image-too-tall-error',
    when(image.height / window.innerHeight > 0.4, {
      width: image.width / (4 * (image.height / window.innerHeight)),
      height: image.height / (4 * (image.height / window.innerHeight)),
    }),
  );

const whenSmallImageT = (image: HTMLImageElement): Task<string, ImageSize> =>
  toTask(
    'image-too-small-error',
    when(window.innerWidth / image.width > 4, { width: image.width, height: image.height }),
  );

const whenLargerImageT = (image: HTMLImageElement): Task<string, ImageSize> =>
  toTask(
    'new-dimensions-error',
    just({})
      .assign('width', getDesiredDimension(window.innerWidth))
      .assign(
        'height',
        getDesiredDimension(window.innerWidth).andThen((convertedWidth) =>
          just(convertedWidth / (image.width / image.height)),
        ),
      ),
  );

const HtmlImageView: React.FC<Props> = ({ src }) => {
  const [imageState, setImageState] = React.useState<ImageState>({ kind: 'waiting' });

  const setImageSize = React.useCallback((width: number, height: number) => {
    setImageState({ kind: 'ready', size: { width, height } });
  }, []);

  const setError = React.useCallback(() => {
    setImageState({ kind: 'error' });
  }, []);

  const setImageSizeDepending = React.useCallback(
    (image: HTMLImageElement) => {
      whenTallImageT(image)
        .orElse(() => whenSmallImageT(image))
        .orElse(() => whenLargerImageT(image))
        .fork(noop, ({ width, height }) => setImageSize(width, height));
    },
    [setImageSize],
  );

  React.useEffect(() => {
    const image = new Image();
    image.src = src;

    image.onload = () => setImageSizeDepending(image);
    image.onerror = setError;

    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [src, setImageSizeDepending, setError]);

  switch (imageState.kind) {
    case 'waiting':
      // Reserve the column while the image is still resolving (see
      // styles.css); rendering nothing lets the text take the full width
      // and then reflow once the image arrives. Same as platform3.
      return <span className="html-image-loading" aria-hidden="true" />;
    case 'error':
      return emptyFragment();
    case 'ready':
      return (
        <img
          alt="description"
          src={src}
          style={{
            height: imageState.size.height,
            width: imageState.size.width,
          }}
        />
      );
  }
};

export default observer(HtmlImageView);
